import { z } from "zod";
import { COUNTDOWN_SECONDS_VALUES, DECISION_CHOICES_MAX, DECISION_CHOICES_MIN } from "../../constants/decision.js";
import { ENDING_RARITY_VALUES } from "../../constants/ending-rarity.js";
import { GENRE_VALUES } from "../../constants/genre.js";
import { CHOICE_LABEL_MAX_LENGTH, CHOICE_SUBTITLE_MAX_LENGTH, DECISION_QUESTION_MAX_LENGTH, ENDING_NAME_MAX_LENGTH, STORY_SYNOPSIS_MAX_LENGTH, STORY_TITLE_MAX_LENGTH, STORY_TITLE_MIN_LENGTH, } from "../../constants/limits.js";
import { RENDER_MODE_VALUES } from "../../constants/render-mode.js";
import { STORY_STATUS_VALUES } from "../../constants/story-status.js";
const ALLOWED_COUNTDOWN_SECONDS = new Set(COUNTDOWN_SECONDS_VALUES);
export const PlayerShotSchema = z.object({
    id: z.uuid(),
    renderMode: z.enum(RENDER_MODE_VALUES),
    videoUrl: z.url().nullable(),
    imageUrl: z.url().nullable(),
    durationSeconds: z.number().positive(),
});
export const PlayerChoiceSchema = z.object({
    id: z.uuid(),
    label: z.string().min(1).max(CHOICE_LABEL_MAX_LENGTH),
    subtitle: z.string().max(CHOICE_SUBTITLE_MAX_LENGTH).nullable(),
    nextNodeId: z.uuid(),
});
export const PlayerDecisionSchema = z
    .object({
    id: z.uuid(),
    question: z.string().min(1).max(DECISION_QUESTION_MAX_LENGTH),
    timeoutSeconds: z
        .number()
        .int()
        .refine((value) => ALLOWED_COUNTDOWN_SECONDS.has(value), "Durée de countdown non autorisée"),
    defaultChoiceId: z.uuid(),
    choices: z.array(PlayerChoiceSchema).min(DECISION_CHOICES_MIN).max(DECISION_CHOICES_MAX),
})
    .refine((decision) => decision.choices.some((choice) => choice.id === decision.defaultChoiceId), "defaultChoiceId doit référencer un choix de la décision");
export const PlayerNodeSchema = z
    .object({
    id: z.uuid(),
    shots: z.array(PlayerShotSchema).min(1),
    decision: PlayerDecisionSchema.nullable(),
    nextNodeId: z.uuid().nullable(),
    isTerminal: z.boolean(),
})
    .refine((node) => !(node.decision !== null && node.nextNodeId !== null), "Un nœud ne peut pas être à la fois une décision et une transition directe")
    .refine((node) => !node.isTerminal || (node.decision === null && node.nextNodeId === null), "Un nœud terminal n'a aucune issue")
    .refine((node) => node.isTerminal || node.decision !== null || node.nextNodeId !== null, "Un nœud non terminal doit avoir une issue");
export const EndingSchema = z.object({
    id: z.uuid(),
    name: z.string().min(1).max(ENDING_NAME_MAX_LENGTH),
    number: z.number().int().positive(),
    rarity: z.enum(ENDING_RARITY_VALUES),
    unlockedPercent: z.number().min(0).max(100),
});
export const StoryGraphV2Schema = z
    .object({
    id: z.uuid(),
    seriesId: z.uuid(),
    title: z.string().min(STORY_TITLE_MIN_LENGTH).max(STORY_TITLE_MAX_LENGTH),
    synopsis: z.string().max(STORY_SYNOPSIS_MAX_LENGTH),
    genre: z.enum(GENRE_VALUES),
    status: z.enum(STORY_STATUS_VALUES),
    coverUrl: z.url(),
    entryNodeId: z.uuid(),
    nodes: z.array(PlayerNodeSchema).min(1),
    endings: z.array(EndingSchema).min(1),
})
    .superRefine((graph, ctx) => {
    const nodeIds = new Set(graph.nodes.map((node) => node.id));
    if (!nodeIds.has(graph.entryNodeId)) {
        ctx.addIssue({ code: "custom", path: ["entryNodeId"], message: "entryNodeId doit référencer un nœud du graphe" });
    }
    for (const [nodeIndex, node] of graph.nodes.entries()) {
        if (node.nextNodeId !== null && !nodeIds.has(node.nextNodeId)) {
            ctx.addIssue({
                code: "custom",
                path: ["nodes", nodeIndex, "nextNodeId"],
                message: "nextNodeId doit référencer un nœud du graphe",
            });
        }
        if (node.decision === null) {
            continue;
        }
        for (const [choiceIndex, choice] of node.decision.choices.entries()) {
            if (!nodeIds.has(choice.nextNodeId)) {
                ctx.addIssue({
                    code: "custom",
                    path: ["nodes", nodeIndex, "decision", "choices", choiceIndex, "nextNodeId"],
                    message: "nextNodeId doit référencer un nœud du graphe",
                });
            }
        }
    }
});
